// Redux-toolkit
import { combineReducers } from '@reduxjs/toolkit';
import {
    GROUPES_SLICE_NAME,
    MARQUES_SLICE_NAME,
    POLES_SLICE_NAME,
    SERVICES_SLICE_NAME,
    UTILISATEUR_SLICE_NAME,
    ANTENNES_SLICE_NAME,
    GROUPE_TRAVAIL_SLICE_NAME,
    DOMAINES_SLICE_NAME,
    UTILISATEUR_GROUPE_TRAVAIL_SLICE_NAME,
} from './constants';
import {
    UtilisateursApi,
    UserApi,
    PolesApi,
    DroitsApi,
    GroupesApi,
    JournauxApi,
    MarquesApi,
    ServicesApi,
    AntennesApi,
    DomaineApi,
    GroupesDeTravailApi,
    JointureUtilisateurGroupeTravailApi,
    Configuration as UtilisateursConfiguration,
    AvatarsApi,
} from '../../openApi/ApiUtilisateur';

// Types.
import { type UtilisateursConfigurationParameters } from './types';

// Slices.
import { createUtilisateurSlice } from './slices/utilisateurSlice';
import { createServicesSlice } from './slices/servicesSlice';
import { createPolesSlice } from './slices/polesSlice';
import { createMarquesSlice } from './slices/marquesSlice';
import { createGroupesSlice } from './slices/groupesSlice';
import { createAntennesSlice } from './slices/antennesSlice';
import { createGroupeTravailSlice } from './slices/groupeTravailSlice';
import { createUtilisateurGroupeTravailSlice } from './slices/utilisateursGroupeTravailSlice';
import { createDomainesSlice } from './slices/domainesSlice';

/**
 * Reducers exports.
 *
 * returns reducer
 * @param configuration Api's configuration
 */
export function createUtilisateurReducer(configuration: UtilisateursConfigurationParameters) {
    const { utilisateurSlice, utilisateurDataSourcesThunks } =
        createUtilisateurSlice(configuration);

    return {
        utilisateurSlice,
        utilisateurDataSourcesThunks,
    };
}

export function createServicesReducer(configuration: UtilisateursConfigurationParameters) {
    const { servicesSlice, servicesDataSourcesThunks } = createServicesSlice(configuration);

    return {
        servicesSlice,
        servicesDataSourcesThunks,
    };
}

export function createPolesReducer(configuration: UtilisateursConfigurationParameters) {
    const { polesSlice, polesDataSourcesThunks } = createPolesSlice(configuration);

    return {
        polesSlice,
        polesDataSourcesThunks,
    };
}

export function createMarquesReducer(configuration: UtilisateursConfigurationParameters) {
    const { marquesSlice, marquesDataSourcesThunks } = createMarquesSlice(configuration);

    return {
        marquesSlice,
        marquesDataSourcesThunks,
    };
}

export function createGroupesReducer(configuration: UtilisateursConfigurationParameters) {
    const { groupesSlice, groupesDataSourcesThunks } = createGroupesSlice(configuration);

    return {
        groupesSlice,
        groupesDataSourcesThunks,
    };
}

export function createAntennesReducer(configuration: UtilisateursConfigurationParameters) {
    const { antennesSlice, antennesDataSourcesThunks } = createAntennesSlice(configuration);

    return {
        antennesSlice,
        antennesDataSourcesThunks,
    };
}

export function createDomainesReducer(configuration: UtilisateursConfigurationParameters) {
    const { domainesSlice, domainesDataSourcesThunks } = createDomainesSlice(configuration);

    return {
        domainesSlice,
        domainesDataSourcesThunks,
    };
}

export function createGroupeTravailReducer(configuration: UtilisateursConfigurationParameters) {
    const { groupeTravailSlice, groupeTravailDataSourcesThunks } =
        createGroupeTravailSlice(configuration);

    return {
        groupeTravailSlice,
        groupeTravailDataSourcesThunks,
    };
}

export function createUtilisateurGroupeTravailReducer(
    configuration: UtilisateursConfigurationParameters,
) {
    const { utilisateurGroupeTravailSlice, utilisateurGroupeTravailDataSourcesThunks } =
        createUtilisateurGroupeTravailSlice(configuration);

    return {
        utilisateurGroupeTravailSlice,
        utilisateurGroupeTravailDataSourcesThunks,
    };
}

/**
 * Reducer build
 *
 * return combined previous reducers.
 * @param configuration Api's configuration.
 */
export function buildUtilisateurRedux(configuration: UtilisateursConfigurationParameters) {
    // Create new Api main configuration.
    const utilisateursConfiguration = new UtilisateursConfiguration(configuration);

    // Create new Apis with data from api.ts and base.ts and new configuration.
    const utilisateursApi = new UtilisateursApi(utilisateursConfiguration);
    const userKeycloakApi = new UserApi(utilisateursConfiguration);
    const polesApi = new PolesApi(utilisateursConfiguration);
    const droitsApi = new DroitsApi(utilisateursConfiguration);
    const groupesApi = new GroupesApi(utilisateursConfiguration);
    const journauxApi = new JournauxApi(utilisateursConfiguration);
    const marquesApi = new MarquesApi(utilisateursConfiguration);
    const servicesApi = new ServicesApi(utilisateursConfiguration);
    const antennesApi = new AntennesApi(utilisateursConfiguration);

    // slices variables instanciation
    const domainesApi = new DomaineApi(utilisateursConfiguration);
    const groupesTravailApi = new GroupesDeTravailApi(utilisateursConfiguration);
    const utilisateurGroupeTravailApi = new JointureUtilisateurGroupeTravailApi(
        utilisateursConfiguration,
    );
    const avatarsApi = new AvatarsApi(utilisateursConfiguration);

    // Create new reducers with new configuration.
    const { utilisateurSlice, utilisateurDataSourcesThunks } =
        createUtilisateurReducer(configuration);
    const { servicesSlice, servicesDataSourcesThunks } = createServicesReducer(configuration);
    const { polesSlice, polesDataSourcesThunks } = createPolesReducer(configuration);
    const { marquesSlice, marquesDataSourcesThunks } = createMarquesReducer(configuration);
    const { groupesSlice, groupesDataSourcesThunks } = createGroupesReducer(configuration);
    const { antennesSlice, antennesDataSourcesThunks } = createAntennesReducer(configuration);
    const { domainesSlice, domainesDataSourcesThunks } = createDomainesReducer(configuration);
    const { groupeTravailSlice, groupeTravailDataSourcesThunks } =
        createGroupeTravailReducer(configuration);
    const { utilisateurGroupeTravailSlice, utilisateurGroupeTravailDataSourcesThunks } =
        createUtilisateurGroupeTravailReducer(configuration);

    // Add all slices in the utilisateur reducer (access by utilisateur section in the store)
    // Combine reducers.
    const combinedUtilisateursReducers = combineReducers({
        [UTILISATEUR_SLICE_NAME]: utilisateurSlice.reducer,
        [SERVICES_SLICE_NAME]: servicesSlice.reducer,
        [POLES_SLICE_NAME]: polesSlice.reducer,
        [MARQUES_SLICE_NAME]: marquesSlice.reducer,
        [GROUPES_SLICE_NAME]: groupesSlice.reducer,
        [ANTENNES_SLICE_NAME]: antennesSlice.reducer,
        [DOMAINES_SLICE_NAME]: domainesSlice.reducer,
        [GROUPE_TRAVAIL_SLICE_NAME]: groupeTravailSlice.reducer,
        [UTILISATEUR_GROUPE_TRAVAIL_SLICE_NAME]: utilisateurGroupeTravailSlice.reducer,
    });

    return {
        serviceConfigurationParameters: configuration,
        serviceConfiguration: utilisateursConfiguration,

        reducer: combinedUtilisateursReducers,

        apis: {
            utilisateursApi,
            userKeycloakApi,
            polesApi,
            droitsApi,
            groupesApi,
            journauxApi,
            marquesApi,
            servicesApi,
            antennesApi,
            domainesApi,
            groupesTravailApi,
            utilisateurGroupeTravailApi,
            avatarsApi,
        },

        slices: {
            utilisateurSlice,
            servicesSlice,
            polesSlice,
            marquesSlice,
            groupesSlice,
            antennesSlice,
            groupeTravailSlice,
            domainesSlice,
            utilisateurGroupeTravailSlice,
        },

        thunks: {
            utilisateurDataSourcesThunks,
            servicesDataSourcesThunks,
            polesDataSourcesThunks,
            marquesDataSourcesThunks,
            groupesDataSourcesThunks,
            antennesDataSourcesThunks,
            groupeTravailDataSourcesThunks,
            domainesDataSourcesThunks,
            utilisateurGroupeTravailDataSourcesThunks,
        },

        fetchItems: {
            getUtilisateurs: utilisateurDataSourcesThunks.main.getData,
            getAutoSearch: utilisateurDataSourcesThunks.autosearch.getData,
            getServices: servicesDataSourcesThunks.main.getData,
            getPoles: polesDataSourcesThunks.main.getData,
            getMarques: marquesDataSourcesThunks.main.getData,
            getGroupes: groupesDataSourcesThunks.main.getData,
            getAntennes: antennesDataSourcesThunks.main.getData,
            getGroupesTravail: groupeTravailDataSourcesThunks.main.getData,
            getDomaines: domainesDataSourcesThunks.main.getData,
            getUtilisateurGroupeTravail: utilisateurGroupeTravailDataSourcesThunks.main.getData,
            getGroupeTravailActiveInscriptions:
                utilisateurGroupeTravailDataSourcesThunks.groupeTravailActiveInscriptions.getData,
        },

        getItems: {
            selectUtilisateurs: utilisateurDataSourcesThunks.main.getSelected,
            selectAntennes: antennesDataSourcesThunks.main.getSelected,
            selectAutoSearch: utilisateurDataSourcesThunks.autosearch.getSelected,
            selectGroupesTravail: groupeTravailDataSourcesThunks.main.getSelected,
        },

        models: {},
    };
}
